import React, { useEffect, useState, useCallback } from "react";
import { getPrescriptionList, getPrescriptionByDate } from "../services/apiServices";
import {
	Box,
	Paper,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Divider,
	IconButton,
	CircularProgress,
	Alert,
	Skeleton,
	Snackbar,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
	ExpandMore,
	CalendarMonth,
	RefreshOutlined,
	DescriptionOutlined,
	Close as CloseIcon,
} from "@mui/icons-material";
import { parse, format } from "date-fns";
import Api from "../Api/Api.json";
import config from "../config/config";
import PhotoPrescriptionPreview from "./PhotoPrescriptionPreview";

const PreviousPrescription = ({ patient_id }) => {
	const [records, setRecords] = useState([]);
	const [expandedPanel, setExpandedPanel] = useState(false);
	const [loading, setLocalLoading] = useState(true);
	const [error, setError] = useState(null);
	const [patientId, setPatientId] = useState(patient_id || 0);

	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "info",
	});

	// QA dialog state
	const [showQADialog, setShowQADialog] = useState(false);
	const [qaLoading, setQaLoading] = useState(false);
	const [qaError, setQaError] = useState("");
	const [qaResult, setQaResult] = useState(null);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Show all records without pagination
	const currentRecords = records;

	// Fetch prescription records
	const fetchRecords = useCallback(async () => {
		setLocalLoading(true);
		setError(null);

		try {
			const result = await getPrescriptionList(patientId);
			console.log(result);
			if (result && Array.isArray(result)) {
				const sortedRecords = result.sort((a, b) => {
					const dateA = new Date(a.creation_date);
					const dateB = new Date(b.creation_date);
					return dateB - dateA; // Sort in descending order
				});

				setRecords(sortedRecords);
				if (sortedRecords.length === 0) {
					setNotification({
						open: true,
						message: "No prescription records found",
						severity: "info",
					});
				}
			} else {
				throw new Error("Failed to fetch records");
			}
		} catch (error) {
			console.error("Error fetching records:", error);
			setError(error.message || "Failed to fetch prescription records");
		} finally {
			setLocalLoading(false);
		}
	}, [patientId]);

	// Handle record expansion and fetch details
	const handlePanelChange = useCallback(
		async (panel, record) => {
			setExpandedPanel(expandedPanel === panel ? false : panel);

			if (expandedPanel !== panel && !record.prescription) {
				try {
					// Fetch record details
					const dataResponse = await getPrescriptionByDate(patientId, record.creation_date);
					console.log("Prescription details:", dataResponse);

					if (dataResponse && Array.isArray(dataResponse) && dataResponse.length > 0) {
						// Parse the prescription JSON string
						const prescriptionData = dataResponse[0];
						let parsedPrescription = prescriptionData;
						
						if (prescriptionData.prescription && typeof prescriptionData.prescription === 'string') {
							try {
								const parsed = JSON.parse(prescriptionData.prescription);
								// Map the parsed fields to the expected format
								parsedPrescription = {
									...prescriptionData,
									// Map complaints to cc_list as JSON string
									cc_list: parsed.complaints ? JSON.stringify({ complaint: parsed.complaints }) : "{}",
									// Map on_exam and diagnosis to advices_list as JSON string
									advices_list: JSON.stringify({
										on_exam: parsed.on_exam || "",
										diagnosis: parsed.diagnosis || ""
									}),
									// Add advices as a separate field
									advices: parsed.advices || "",
									// Map medicine array to medicine_list as JSON string
									medicine_list: parsed.medicine ? 
										JSON.stringify(parsed.medicine.reduce((acc, med, index) => {
											const medicineStr = `${med.name} ${med.strength || ""} (${med.type || ""}) - ${med.dosage || ""} ${med.timing || ""} for ${med.continueTill || ""}`;
											const indicationStr = med.indication ? ` - Indication: ${med.indication}` : "";
											acc[`medicine_${index}`] = medicineStr + indicationStr;
											return acc;
										}, {})) : "{}",
									// Keep original fields for backward compatibility
									...parsed
								};
							} catch (error) {
								console.error("Error parsing prescription JSON:", error);
								parsedPrescription = prescriptionData;
							}
						}
						
						// Also parse medicine_usage if it exists and convert to medicine_usage_list
						if (prescriptionData.medicine_usage && typeof prescriptionData.medicine_usage === 'string') {
							try {
								const parsedUsage = JSON.parse(prescriptionData.medicine_usage);
								parsedPrescription.medicine_usage_list = JSON.stringify(parsedUsage);
							} catch (error) {
								console.error("Error parsing medicine_usage JSON:", error);
								parsedPrescription.medicine_usage_list = "[]";
							}
						} else {
							parsedPrescription.medicine_usage_list = "[]";
						}
						
						console.log("Parsed prescription data:", parsedPrescription);
						setRecords((prev) =>
							prev.map((item) =>
								item.creation_date === record.creation_date
									? {
											...item,
											prescription: parsedPrescription,
									  }
									: item
							)
						);
					} else {
						throw new Error("Failed to fetch record details");
					}
				} catch (error) {
					console.error("Error fetching record details:", error);
					setNotification({
						open: true,
						message: "Failed to fetch prescription details",
						severity: "error",
					});
				}
			}
		},
		[expandedPanel, patientId]
	);



	// Format date and time
	const formatDateTime = useCallback((dateTime) => {
		try {
			const parsedDate = parse(
				dateTime,
				"yyyy-MM-dd HH:mm:ss",
				new Date()
			);
			return format(parsedDate, "MMMM dd, yyyy, hh:mm a");
		} catch (error) {
			console.error("Date parsing error:", error);
			return dateTime;
		}
	}, []);

	// Close notification
	const handleCloseNotification = useCallback(() => {
		setNotification((prev) => ({ ...prev, open: false }));
	}, []);

	// Refresh records
	const handleRefresh = useCallback(() => {
		fetchRecords();
		setNotification({
			open: true,
			message: "Refreshing prescription records...",
			severity: "info",
		});
	}, [fetchRecords]);

	// Build prescription_dict from record.prescription (same shape used for preview)
	const buildPrescriptionDict = useCallback((prescription) => {
		if (!prescription) return null;
		const advicesList = (() => {
			try {
				return typeof prescription.advices_list === "string"
					? JSON.parse(prescription.advices_list || "{}")
					: prescription.advices_list || {};
			} catch {
				return {};
			}
		})();
		return {
			complaints: prescription.complaints ?? "",
			on_exam: prescription.on_exam ?? advicesList.on_exam ?? "",
			diagnosis: prescription.diagnosis ?? advicesList.diagnosis ?? "",
			advices: prescription.advices ?? "",
			pathology_tests: Array.isArray(prescription.pathology_tests) ? prescription.pathology_tests : [],
			radiology_tests: Array.isArray(prescription.radiology_tests) ? prescription.radiology_tests : [],
			medicine: Array.isArray(prescription.medicine) ? prescription.medicine : [],
			patient_name: prescription.patient_name ?? prescription.name ?? "",
			patient_age: prescription.patient_age ?? prescription.age ?? "",
			patient_gender: prescription.patient_gender ?? "",
			doctor_name: prescription.doctor_name ?? "",
		};
	}, []);

	// Run QA for a prescription item
	const handleRunQAForRecord = useCallback(
		async (prescriptionDict, prescDate) => {
			setShowQADialog(true);
			setQaLoading(true);
			setQaError("");
			setQaResult(null);
			try {
				const requestData = {
					task: "getPrescriptionQA",
					patient_id: patientId,
					presc_date: prescDate,
					prescription_dict: prescriptionDict,
				};
				const response = await fetch(Api.PRESCRIPTION_DATA_FETCH, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(requestData),
				});
				if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
				let data = await response.json();
				if (data && typeof data.body === "string") {
					try {
						data = JSON.parse(data.body);
					} catch (_) {}
				} else if (typeof data === "string") {
					try {
						data = JSON.parse(data);
					} catch (_) {
						data = {};
					}
				}
				if (data && typeof data.data === "object" && data.data !== null) data = data.data;
				const qa = data || {};
				setQaResult({
					complaints: qa.complaints ?? "",
					medicine: qa.medicine ?? "",
					pathology_tests: qa.pathology_tests ?? "",
					radiology_tests: qa.radiology_tests ?? "",
					diagnosis: qa.diagnosis ?? "",
					quality_score: qa.quality_score ?? "",
				});
			} catch (e) {
				setQaError(e?.message || "Failed to run QA.");
			} finally {
				setQaLoading(false);
			}
		},
		[patientId]
	);

	// Update patientId when prop changes and fetch records
	useEffect(() => {
		const newPatientId = patient_id || 0;
		setPatientId(newPatientId);
		
		// Clear existing records when patient changes
		setRecords([]);
		setExpandedPanel(false);
		
		// Fetch records for the new patient
		if (newPatientId) {
			fetchRecords();
		}
	}, [patient_id, fetchRecords]);

	return (
		<Box
			className="max-w-3xl mx-auto px-4 py-6"
			style={{ minHeight: `calc(100vh - ${config.topbarHeight}px)` }}
		>
			<Box className="flex justify-between items-center mb-6">
				<Typography
					variant="h4"
					component="h1"
					className="font-bold text-blue-800"
				>
					Previous Prescriptions
				</Typography>
				<Tooltip title="Refresh records">
					<IconButton onClick={handleRefresh} color="primary">
						<RefreshOutlined />
					</IconButton>
				</Tooltip>
			</Box>

			{loading ? (
				// Loading state
				Array.from(new Array(3)).map((_, index) => (
					<Paper key={index} className="mb-4 overflow-hidden">
						<Box className="p-4">
							<Skeleton variant="rectangular" height={60} />
						</Box>
					</Paper>
				))
			) : error ? (
				// Error state
				<Alert
					severity="error"
					action={
						<LoadingButton onClick={fetchRecords}>
							Retry
						</LoadingButton>
					}
					className="mb-4"
				>
					{error}
				</Alert>
			) : records.length === 0 ? (
				// Empty state
				<Paper className="p-6 text-center">
					<Typography variant="h6" className="mb-2 text-gray-600">
						No prescription records found
					</Typography>
					<Typography variant="body2" className="mb-4 text-gray-500">
						Any prescriptions you upload will appear here
					</Typography>
					<LoadingButton
						variant="outlined"
						onClick={handleRefresh}
						startIcon={<RefreshOutlined />}
					>
						Refresh
					</LoadingButton>
				</Paper>
			) : (
				// Records list
				<>
					{currentRecords.map((record, index) => (
								<Accordion
									key={record.creation_date}
									expanded={
										expandedPanel === record.creation_date
									}
									onChange={() =>
										handlePanelChange(
											record.creation_date,
											record
										)
									}
									className="mb-4 shadow-md rounded-lg overflow-hidden border border-gray-100"
								>
									<AccordionSummary
										expandIcon={
											<IconButton
												size="small"
												className="bg-blue-50"
											>
												<ExpandMore />
											</IconButton>
										}
										className="hover:bg-blue-50 transition-colors"
									>
										<Box className="flex items-center w-full gap-3">
											<CalendarMonth className="text-blue-600" />
											<Typography
												variant="subtitle1"
												className="font-medium"
											>
												{formatDateTime(
													record.creation_date
												)}
											</Typography>
										</Box>
									</AccordionSummary>

									<Divider />

									<AccordionDetails className="p-4">
										{!record.prescription ? (
											<Box className="flex justify-center p-6">
												<CircularProgress
													size={40}
												/>
											</Box>
										) : (
											<PhotoPrescriptionPreview
												prescription={
													record.prescription
												}
												prescriptionDict={buildPrescriptionDict(record.prescription)}
												prescDate={record.creation_date}
												onRunQA={handleRunQAForRecord}
											/>
										)}
									</AccordionDetails>
								</Accordion>
						  ))}


				</>
			)}

			{/* QA Dialog */}
			{showQADialog && (
				<Box
					className="fixed inset-0 flex justify-center items-center z-50"
					sx={{ bgcolor: "rgba(0,0,0,0.5)" }}
					onClick={() => {
						if (!qaLoading) {
							setShowQADialog(false);
							setQaError("");
							setQaResult(null);
							setQaLoading(false);
						}
					}}
				>
					<Paper
						className="w-full max-w-4xl max-h-[90%] overflow-y-auto p-6 relative"
						onClick={(e) => e.stopPropagation()}
					>
						<Box className="flex items-center justify-between mb-4">
							<Typography variant="h6" className="font-bold text-gray-800">
								Prescription QA
							</Typography>
							<IconButton
								size="small"
								onClick={() => {
									setShowQADialog(false);
									setQaError("");
									setQaResult(null);
									setQaLoading(false);
								}}
							>
								<CloseIcon />
							</IconButton>
						</Box>
						{qaLoading && (
							<Box className="flex items-center gap-2 py-4">
								<CircularProgress size={24} />
								<Typography>Running QA...</Typography>
							</Box>
						)}
						{!qaLoading && qaError && (
							<Alert severity="error" className="mb-4">
								{qaError}
							</Alert>
						)}
						{!qaLoading && !qaError && qaResult && (
							<Box className="space-y-4">
								<Box className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
									<Typography variant="body2" className="text-teal-800 font-semibold">
										Quality Score
									</Typography>
									<Typography variant="h4" className="font-bold text-teal-900">
										{qaResult.quality_score}
									</Typography>
								</Box>
								<Box>
									<Typography variant="subtitle2" className="font-semibold text-gray-700 mb-1">
										Complaints
									</Typography>
									<Typography variant="body2" className="text-gray-800 whitespace-pre-wrap">
										{qaResult.complaints}
									</Typography>
								</Box>
								<Box>
									<Typography variant="subtitle2" className="font-semibold text-gray-700 mb-1">
										Medicine
									</Typography>
									<Typography variant="body2" className="text-gray-800 whitespace-pre-wrap">
										{qaResult.medicine}
									</Typography>
								</Box>
								<Box>
									<Typography variant="subtitle2" className="font-semibold text-gray-700 mb-1">
										Pathology Tests
									</Typography>
									<Typography variant="body2" className="text-gray-800 whitespace-pre-wrap">
										{qaResult.pathology_tests}
									</Typography>
								</Box>
								<Box>
									<Typography variant="subtitle2" className="font-semibold text-gray-700 mb-1">
										Radiology Tests
									</Typography>
									<Typography variant="body2" className="text-gray-800 whitespace-pre-wrap">
										{qaResult.radiology_tests}
									</Typography>
								</Box>
								<Box>
									<Typography variant="subtitle2" className="font-semibold text-gray-700 mb-1">
										Diagnosis
									</Typography>
									<Typography variant="body2" className="text-gray-800 whitespace-pre-wrap">
										{qaResult.diagnosis}
									</Typography>
								</Box>
							</Box>
						)}
					</Paper>
				</Box>
			)}

			{/* Notification */}
			<Snackbar
				open={notification.open}
				autoHideDuration={5000}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant="filled"
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default PreviousPrescription;
